import { useRef, useState } from 'react'
import { useClickAway } from 'react-use'

import caretIcon from 'assets/images/explore-learnings/caret.svg'
import checkActiveIcon from 'assets/images/explore-learnings/check-active.svg'
import checkInactiveIcon from 'assets/images/explore-learnings/check-inactive.svg'
import searchIcon from 'assets/images/explore-learnings/search.svg'

interface SelectMultipleProps {
  options?: { key: string; value: string }[]
  placeholder?: string
  searchPlaceholder?: string
  selectValue: string[]
  onChange: (values: string[]) => void
  selectWidth?: string
  dropdownWidth?: string
  hasError?: boolean
}

export const SelectMultiple = ({
  options,
  placeholder = 'Select options',
  searchPlaceholder = 'Search',
  selectValue,
  onChange: setSelectValue,
  selectWidth = '198px',
  dropdownWidth = '256px',
  hasError = false,
}: SelectMultipleProps) => {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const selectRef = useRef(null)
  const uid = `select-single-${Math.random().toString(36).substr(2, 9)}`
  useClickAway(selectRef, e => {
    const selectedClasses = (e.target as HTMLElement | null)?.getAttribute('class')
    if (selectedClasses?.includes(uid)) {
      return
    }
    setOpen(false)
  })
  const selectedText =
    selectValue.length > 0
      ? selectValue.map(option => options?.find(el => el.value === option)?.key).join(',')
      : placeholder
  const onSelectedValue = (option: { key: string; value: string }) => {
    if (selectValue.includes(option.value)) {
      const result = selectValue.filter(field => field !== option.value)
      setSelectValue(result)
    } else {
      setSelectValue([...selectValue, option.value])
    }
  }

  const isActive = open || selectValue.length > 0

  return (
    <div className="relative" style={{ width: selectWidth }}>
      <div
        className={`
          py-2 px-[10px] border bg-white border-solid rounded-8 flex items-center cursor-pointer text-neutral-70 ${uid}
          ${isActive ? 'text-black' : ''}
          ${open ? 'border-black' : ''}
          ${hasError ? 'border-red-60' : 'border-neutral-60'}
        `}
        onClick={() => setOpen(!open)}
      >
        <span className={`rounded-8 overflow-ellipsis break-all body-s w-full line-clamp-1 ${uid}`}>
          {' '}
          {selectedText}{' '}
        </span>
        {selectValue.length > 0 && (
          <span
            className={`flex ${uid} size-[18px] items-center justify-center rounded-full bg-black text-xs font-bold text-white w-[23px] gap-1`}
          >
            {selectValue.length}
          </span>
        )}
        <img
          src={caretIcon}
          alt="Caret"
          className={`ml-2 ${uid} transition-all ease-in-out justify-self-end ${open ? 'rotate-180' : ''}`}
        />
      </div>
      <div
        className={`${open ? 'block' : 'hidden'} absolute mt-2 bg-white rounded-8 z-10 text-14 shadow-md`}
        style={{ width: dropdownWidth }}
        ref={selectRef}
      >
        <div className="w-full py-[11px] px-2 flex border-b border-solid border-[#6E6E6E] items-center">
          <img src={searchIcon} className="w-4 h-4 mr-1" alt="Search" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className=" h-[22px] body-s outline-none"
            placeholder={searchPlaceholder}
          />
        </div>
        <div className="max-h-40 overflow-y-auto p-2 scroller">
          {options
            ?.filter(option => option.key.toLowerCase().includes(search.toLowerCase()))
            .map(option => (
              <div key={option.value} className={`flex items-center px-2 py-[6px] mb-1 text-ellipsis ${dropdownWidth}`}>
                <label className="flex cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectValue.includes(option.value)}
                    onChange={() => {
                      onSelectedValue(option)
                    }}
                    className="peer hidden"
                  />
                  <img src={checkActiveIcon} className="hidden peer-checked:block" alt="Active" />
                  <img src={checkInactiveIcon} className="peer-not-checked:block peer-checked:hidden" alt="Inactive" />
                  <span className="text-[#505050] body-s peer-checked:font-semibold peer-checked:text-black ml-2 break-all line-clamp-1">
                    {option.key}
                  </span>
                </label>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
